import { useState, useEffect, useCallback, useRef } from 'react';

export default function PhotoCarousel({ photos }) {
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const count = photos.length;

  const goTo = useCallback((i) => {
    const next = ((i % count) + count) % count;
    if (next === indexRef.current) return;
    indexRef.current = next;
    setIndex(next);
  }, [count]);

  const interact = useCallback((i) => {
    pausedRef.current = true;
    goTo(i);
  }, [goTo]);

  const prev = () => interact(indexRef.current - 1);
  const next = () => interact(indexRef.current + 1);

  useEffect(() => {
    photos.forEach((photo) => {
      const img = new Image();
      img.src = photo.src;
    });
  }, [photos]);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || count < 2) return undefined;

    const id = setInterval(() => {
      if (pausedRef.current) return;
      goTo(indexRef.current + 1);
    }, 5000);

    return () => clearInterval(id);
  }, [count, goTo]);

  useEffect(() => {
    if (!pausedRef.current) return undefined;

    const id = setTimeout(() => {
      pausedRef.current = false;
    }, 10000);

    return () => clearTimeout(id);
  }, [index]);

  if (!count) return null;

  return (
    <div className="photo-carousel">
      <div className="photo-carousel__viewport">
        {photos.map((photo, i) => (
          <img
            key={`${photo.alt}-${i}`}
            src={photo.src}
            alt={i === index ? photo.alt : ''}
            className={`photo-carousel__image ${i === index ? 'photo-carousel__image--active' : ''}`}
            aria-hidden={i !== index}
            draggable={false}
          />
        ))}
      </div>
      {count > 1 && (
        <>
          <button type="button" className="photo-carousel__arrow photo-carousel__arrow--prev" onClick={prev} aria-label="Previous slide">
            ‹
          </button>
          <button type="button" className="photo-carousel__arrow photo-carousel__arrow--next" onClick={next} aria-label="Next slide">
            ›
          </button>
          <div className="photo-carousel__dots" role="tablist" aria-label="Photo slides">
            {photos.map((photo, i) => (
              <button
                key={`dot-${photo.alt}-${i}`}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Slide ${i + 1}: ${photo.alt}`}
                className={`photo-carousel__dot ${i === index ? 'photo-carousel__dot--active' : ''}`}
                onClick={() => interact(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
